import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";
import "./Legal.css";

const Privacy = () => {
  const { i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState(null);
  

  const sections = [
    {
      id: 1,
      icon: "fa-solid fa-circle-info",
      title: "Introduction",
      content: "Welcome to Solvio Tech. We are committed to protecting your personal information and your right to privacy in accordance with the <strong>Data Protection Act, 2019 (Kenya)</strong> and all applicable Kenyan laws. This policy explains how we collect, use, store, and safeguard your personal data when you visit our website or use our services. By using our website, you consent to the practices described in this policy."
    },
    {
      id: 2,
      icon: "fa-solid fa-scale-balanced",
      title: "Legal Basis for Processing Personal Data",
      content: "Under the Data Protection Act, 2019 (Kenya), we process your personal data on the following legal bases: <strong>your consent</strong>, which you may withdraw at any time; <strong>performance of a contract</strong> between you and Solvio Tech; <strong>compliance with a legal obligation</strong>; and our <strong>legitimate interests</strong> in operating and improving our services."
    },
    {
      id: 3,
      icon: "fa-solid fa-database",
      title: "Information We Collect",
      content: "In accordance with Section 25 of the Data Protection Act, 2019, we only collect data that is adequate, relevant, and limited to what is necessary. This includes:",
      bullets: [
        "Your full name and email address when you contact us",
        "Project details shared via our contact form",
        "Account information if you register on our platform",
        "Google profile information if you sign in with Google OAuth",
        "Technical data such as IP address and browser type via Google Analytics",
        "Payment information processed securely through our payment providers"
      ]
    },
    {
      id: 4,
      icon: "fa-solid fa-gears",
      title: "How We Use Your Information",
      content: "We use your personal data to:",
      bullets: [
        "Respond to your inquiries and deliver our services",
        "Send project updates and invoices",
        "Improve our website and user experience",
        "Comply with legal obligations under Kenyan law",
        "Detect and prevent fraud or unauthorized access as required under the Computer Misuse and Cybercrimes Act, 2018"
      ]
    },
    {
      id: 5,
      icon: "fa-solid fa-shield-halved",
      title: "Data Storage and Security",
      content: "In compliance with Section 41 of the Data Protection Act, 2019, we implement appropriate technical and organizational security measures. All data is transmitted over <strong>HTTPS encryption</strong>, stored on <strong>secure servers</strong> with access controls, and subject to regular security audits. We <strong>never sell, rent, or trade</strong> your personal data to third parties. In the event of a data breach, we will notify affected individuals and the Office of the Data Protection Commissioner (ODPC) within <strong>72 hours</strong> as required by law."
    },
    {
      id: 6,
      icon: "fa-solid fa-clock-rotate-left",
      title: "Data Retention",
      content: "We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy, or as required by Kenyan law. Client project data is retained for a minimum of <strong>7 years</strong> in compliance with tax and business record requirements under Kenyan law."
    },
    {
      id: 7,
      icon: "fa-solid fa-cookie-bite",
      title: "Cookies",
      content: "We use cookies to improve your browsing experience. Under the Kenya Information and Communications Act (KICA), 1998, you have the right to be informed about cookies. You may disable cookies in your browser settings at any time without affecting your ability to use our website."
    },
    {
      id: 8,
      icon: "fa-solid fa-handshake",
      title: "Third-Party Services",
      content: "We use the following third-party services, each governed by their own privacy policies:",
      bullets: [
        "Google Analytics — for website traffic analysis",
        "Google OAuth — for secure sign-in functionality",
        "Payment processors — for secure transaction handling"
      ],
      footer: "We ensure all third-party providers maintain adequate data protection standards consistent with the Data Protection Act, 2019."
    },
    {
      id: 9,
      icon: "fa-solid fa-user-shield",
      title: "Your Rights",
      content: "Under the Data Protection Act, 2019, you have the following rights:",
      bullets: [
        "Access a copy of your personal data",
        "Request correction of inaccurate data",
        "Request deletion of your personal data",
        "Receive your data in a portable format",
        "Object to processing of your personal data",
        "Withdraw consent at any time"
      ],
      footer: "To exercise any of these rights, contact us at info@solviotech.com. We will respond within 21 days as required by law."
    },
    {
      id: 10,
      icon: "fa-solid fa-comment-dots",
      title: "Complaints",
      content: "If you believe your data protection rights have been violated, you may lodge a complaint with the <strong>Office of the Data Protection Commissioner (ODPC)</strong> at <a href='https://www.odpc.go.ke' target='_blank' rel='noopener noreferrer' class='legal-link'>www.odpc.go.ke</a> or <a href='mailto:info@odpc.go.ke' class='legal-link'>info@odpc.go.ke</a>."
    },
    {
      id: 11,
      icon: "fa-solid fa-envelope",
      title: "Contact Us",
      content: "For any privacy-related questions or to exercise your rights:",
      bullets: [
        "Email: info@solviotech.com",
        "Phone: +254 720 955 034 / +254 757 913 737",
        "Address: 1st Kaunda Street, Nairobi, Kenya 🇰🇪"
      ]
    }
  ];

  return (
    <div className="legal-page">

      {/* ── Hero ── */}
      <section className="legal-hero">
        <div className="legal-hero-bg">
          <div className="legal-orb legal-orb--1"></div>
          <div className="legal-orb legal-orb--2"></div>
          <div className="legal-hero-grid"></div>
        </div>
        <div className="legal-hero-inner">
          <div className="legal-breadcrumb">
            <Link to="/" className="legal-breadcrumb-link">Home</Link>
            <i className="fa-solid fa-chevron-right"></i>
            <span>Privacy Policy</span>
          </div>
          {i18n.language !== "en" && (
          <div className="legal-lang-notice">
            <i className="fa-solid fa-circle-info"></i>
              {i18n.language === "sw"
                ? "Hati hii inapatikana kwa Kiingereza tu. Maandishi ya kisheria yanafunga kwa Kiingereza."
                : "Ce document est disponible en anglais uniquement. Le texte juridique est contraignant en anglais."}
            </div>
          )}
          <div className="legal-hero-badge">
            <i className="fa-solid fa-shield-halved"></i>
            <span>Data Protection Act, 2019 (Kenya) Compliant</span>
          </div>
          <h1 className="legal-hero-title">Privacy Policy</h1>
          <p className="legal-hero-sub">
            Your privacy matters to us. This policy explains how Solvio Tech collects,
            uses, and protects your personal data — clearly and transparently.
          </p>
          <div className="legal-meta">
            <span><i className="fa-solid fa-calendar"></i> Last updated: May 2026</span>
            <span className="legal-meta-sep">·</span>
            <span><i className="fa-solid fa-location-dot"></i> Solvio Tech, Nairobi, Kenya</span>
            <span className="legal-meta-sep">·</span>
            <span><i className="fa-solid fa-language"></i> {sections.length} sections</span>
          </div>
        </div>
      </section>

      {/* ── Table of Contents ── */}
      <section className="legal-toc-section">
        <div className="legal-container">
          <div className="legal-toc">
            <h3 className="legal-toc-title"><i className="fa-solid fa-list"></i> Contents</h3>
            <div className="legal-toc-grid">
              {sections.map((s) => (
                <a key={s.id} href={`#section-${s.id}`} className="legal-toc-item">
                  <span className="legal-toc-num">{String(s.id).padStart(2, "0")}</span>
                  <span>{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="legal-content-section">
        <div className="legal-container">
          <div className="legal-sections">
            {sections.map((s) => (
              <div
                key={s.id}
                id={`section-${s.id}`}
                className={`legal-section ${activeSection === s.id ? "legal-section--active" : ""}`}
                onClick={() => setActiveSection(activeSection === s.id ? null : s.id)}
              >
                <div className="legal-section-header">
                  <div className="legal-section-icon"><i className={s.icon}></i></div>
                  <div className="legal-section-meta">
                    <span className="legal-section-num">{String(s.id).padStart(2, "0")}</span>
                    <h2 className="legal-section-title">{s.title}</h2>
                  </div>
                </div>
                <div className="legal-section-body">
                  <p dangerouslySetInnerHTML={{ __html: s.content }}></p>
                  {s.bullets && (
                    <ul className="legal-bullets">
                      {s.bullets.map((b, i) => (
                        <li key={i}><i className="fa-solid fa-check"></i><span>{b}</span></li>
                      ))}
                    </ul>
                  )}
                  {s.footer && <p className="legal-section-footer">{s.footer}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="legal-cta">
        <div className="legal-cta-inner">
          <div className="legal-cta-orb"></div>
          <i className="fa-solid fa-envelope legal-cta-icon"></i>
          <h2 className="legal-cta-title">Have a Privacy Question?</h2>
          <p className="legal-cta-sub">We're committed to transparency. Reach out anytime and we'll respond within 21 days.</p>
          <div className="legal-cta-actions">
            <Link to="/contact" className="legal-btn-primary">
              <i className="fa-solid fa-paper-plane"></i> Contact Us
            </Link>
            <Link to="/terms" className="legal-btn-secondary">
              Terms of Service <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Privacy;
